import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCsrfToken } from '../../utils/csrf';
import { IncomeData, TrendData, IncomeOverview } from '../models/ModelIncome';


export const useIncomeDash = () => {
  const navigate = useNavigate();
  const [incomeData, setIncomeData] = useState<IncomeData[]>([]);
  const [trendData, setTrendData] = useState<TrendData | null>(null);
  const [overview, setOverview] = useState<IncomeOverview | null>(null);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchIncomeDataRef = useRef<(() => Promise<void>) | null>(null);

  const checkRecalculationStatus = useCallback(async () => {
    const checkStatus = async () => {
      try {
        const csrfToken = await getCsrfToken();
        const response = await fetch('/api/income/recalc-status/', {
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'X-CSRFToken': csrfToken,
          }
        });
        const data = await response.json();
        
        if (data.status === 'done') {
          setIsRecalculating(false);
          await fetchIncomeDataRef.current?.();
        } else if (data.status === 'error') {
          setIsRecalculating(false);
        } else {
          const POLLING_INTERVAL_MS = 60 * 60 * 1000;  // minutes * seconds * milliseconds = 1 hour
          setTimeout(checkStatus, POLLING_INTERVAL_MS);
        }
      } catch (error) {
        console.error('Error checking recalculation status:', error);
        setIsRecalculating(false);
      }
    };
    
    checkStatus();
  }, []);

  fetchIncomeDataRef.current = useCallback(async () => {
    try {
      setIsLoading(true);
      const csrfToken = await getCsrfToken();
      
      const response = await fetch('/api/income/dashboard/', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      });

      if (response.redirected) {
        window.location.href = '/login';
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success || !result.has_income_data) {
        navigate('/income/import');
        return;
      }
      
      setIncomeData(result.data.income_history);
      setTrendData(result.data.trend_data);
      setOverview(result.data.overview);

      if (result.recalculating) {
        setIsRecalculating(true);
        checkRecalculationStatus();
      }
    } catch (error) {
      console.error('Error fetching income data:', error);
      navigate('/income/import');
    } finally {
      setIsLoading(false);
    }
  }, [navigate, checkRecalculationStatus]);

  return {
    incomeData,
    trendData,
    overview,
    fetchIncomeData: fetchIncomeDataRef.current,
    isRecalculating,
    isLoading
  };
};